import * as React from 'react';
import {
  type CartCommandEventData,
  cartUpdateEvent,
  isAddLineItemCommand,
  isChangeLineItemQuantityCommand,
  isRemoveLineItemCommand,
} from '@avensia/excite-commercetools/cart/command';
import { addToCartNinetailedEvent, removeCartNinetailedEvent } from 'webapp/ninetailed/tracking';
import { addRetailMediaSponsoredProductAddToCartToDataLayer } from './retail-media';
import {
  cartToCartItems,
  cartToDataLayerInfo,
  convertProductToGA4,
  isPackageLineItem,
  itemPriceToDataLayerInfo,
  getCheckoutPageUrl,
} from '../utils';
import { addToDataLayer, type DataLayerEvent, type EcommerceAdditionAttributes } from '..';
import { ariaLiveObservable } from 'webapp/shared/aria-live-region';
import { translate } from '@avensia/excite';

type CartItem = {
  itemId: string;
  quantity: number;
};

export type CartItems = CartItem[];

type CartTrackingData = {
  cartItems: CartItems;
  cartId: string;
  cartUrl: string;
};

export function mapLineItem(lineItem: Excite.Commercetools.LineItemApiModel, i?: number, quantity?: number) {
  return isPackageLineItem(lineItem)
    ? lineItem.includedInPackage
        .filter(l => l.trackingProductInformation)
        .map(l => ({
          ...convertProductToGA4(l.trackingProductInformation, { quantity }, i),
          ...itemPriceToDataLayerInfo(l.price, l.quantity),
        }))
    : [convertProductToGA4(lineItem.trackingProductInformation, { quantity }, i)];
}

function isAddAccessoryCommand(command: any): command is NordicNest.Api.AddAccessoryCommand {
  return Boolean((command as NordicNest.Api.AddAccessoryCommand).accessoryToSku);
}

function getTotalCartQuantity(cart: Excite.Commercetools.CartApiModel | null | undefined): number {
  if (!cart?.items) {
    return 0;
  }
  return cart.items.reduce((total, item) => total + item.quantity, 0);
}

export function useCartTracking() {
  React.useEffect(
    () =>
      cartUpdateEvent.on(async data => {
        const lineItem = data.lineItem ?? data.cart?.items.find(i => i.sku === data.command.sku);
        const cartUrl = getCheckoutPageUrl();
        const cartTrackingData: CartTrackingData = {
          cartItems: cartToCartItems(data.cart),
          cartId: data.cart?.id ?? '',
          cartUrl: cartUrl ?? '',
        };

        if (lineItem) {
          if (isAddLineItemCommand(data.command)) {
            await addToCartEvent(lineItem, data.command.quantity, data.commandEventData, cartTrackingData);
            ariaLiveObservable.emit({
              message: translate(t => t.product.addToCartMessage, {
                product: lineItem.name,
                count: getTotalCartQuantity(data.cart),
              }),
            });
          } else if (isRemoveLineItemCommand(data.command)) {
            await removeCartEvent(
              lineItem,
              data.commandEventData?.previousQuantity || 1,
              data.commandEventData,
              cartTrackingData,
            );
          } else if (isChangeLineItemQuantityCommand(data.command) && data.commandEventData?.previousQuantity) {
            const previousQuantity = data.commandEventData.previousQuantity;
            const newQuantity = data.command.quantity;
            if (newQuantity > previousQuantity) {
              await addToCartEvent(lineItem, newQuantity - previousQuantity, data.commandEventData, cartTrackingData);
            } else if (newQuantity < previousQuantity) {
              await removeCartEvent(lineItem, previousQuantity - newQuantity, data.commandEventData, cartTrackingData);
            }
          } else if (isAddAccessoryCommand(data.command)) {
            await addToCartEvent(lineItem, data.command.quantity, data.commandEventData, cartTrackingData);
          }
        }
      }),
    [],
  );
}

async function removeCartEvent(
  lineitem: Excite.Commercetools.LineItemApiModel,
  quantity: number,
  commandEventData?: CartCommandEventData,
  cartTrackingData?: CartTrackingData,
) {
  const ga4Event: DataLayerEvent = {
    event: 'remove_from_cart',
    ecommerce: {
      ...((commandEventData?.additionalProductAttributes as EcommerceAdditionAttributes)?.ecommerce ?? {}),
      ...(await cartToDataLayerInfo()),
      items: mapLineItem(lineitem, undefined, quantity),
    },
    cart_items: cartTrackingData?.cartItems,
    cart_id: cartTrackingData?.cartId,
    cart_url: cartTrackingData?.cartUrl,
  };

  addToDataLayer(ga4Event);
  await removeCartNinetailedEvent(lineitem, commandEventData?.previousQuantity || 1, commandEventData);
}

async function addToCartEvent(
  lineitem: Excite.Commercetools.LineItemApiModel,
  quantity: number,
  commandEventData?: CartCommandEventData,
  cartTrackingData?: CartTrackingData,
) {
  const ga4Event: DataLayerEvent = {
    event: 'add_to_cart',
    ecommerce: {
      ...((commandEventData?.additionalProductAttributes as EcommerceAdditionAttributes)?.ecommerce ?? {}),
      ...(await cartToDataLayerInfo()),
      items: mapLineItem(lineitem, undefined, quantity),
    },
    cart_items: cartTrackingData?.cartItems,
    cart_id: cartTrackingData?.cartId,
    cart_url: cartTrackingData?.cartUrl,
  };
  addToDataLayer(ga4Event);
  await addToCartNinetailedEvent(lineitem, commandEventData?.previousQuantity || 1, commandEventData);

  if ((commandEventData?.additionalProductAttributes as EcommerceAdditionAttributes)?.isSponsoredProduct) {
    addRetailMediaSponsoredProductAddToCartToDataLayer(
      (commandEventData?.additionalProductAttributes as EcommerceAdditionAttributes)?.adId,
    );
  }
}
